import React, { useEffect, useState } from 'react';
import { useAppActions, useAppState } from '../../../../overmind';
import { PagePath } from '../../../../navigation/navigation.enums';
import { PageHeader } from '@keplerco/core';
import { useParams } from 'react-router';
import { RoleAverageWidget } from '../../../../components/general/average/role-average/role-average.widget';
import { SkillScoreExpectedProficiencyWidget } from '../skill-score-expected-proficiency.widget';
import { RoleProficiencyRadarWidget } from '../role-proficiency-radar.widget';
import { RoleCompetencyMatrixWidget } from './role-competency-matrix/role-competency-matrix.widget';
import { OrganizationLevelType, ScoreType } from '../../../../enums';
import { ScoreComparison } from '../../../../enums/score-comparison';
import { ScoresSearchParams } from '../../../../models/overmind/search-params';
import { extractHighestOrganizationLevel } from '../../../../library/helpers/permissions/extract-highest-organization-level';
import { OrganizationLevel } from '../../../../models/overmind/permissions';

export function RolePage(): JSX.Element {
  const { roleSlug } = useParams();

  const actions = useAppActions();
  const { companyVariables, user, permissions } = useAppState();
  const organizationLevelType: OrganizationLevelType = React.useMemo(
    () => (user?.isSystemAdmin ? OrganizationLevelType.Company : extractHighestOrganizationLevel(permissions?.analytics.organizationLevels)?.organizationLevel ?? OrganizationLevelType.Learner),
    [user?.isSystemAdmin, permissions?.analytics.organizationLevels]
  );
  const organizationLevel: OrganizationLevel | undefined = React.useMemo(() => permissions?.analytics.organizationLevels.find(organizationLevel => organizationLevel.organizationLevel === organizationLevelType), [permissions?.analytics.organizationLevels, organizationLevelType]);

  const [roleName, setRoleName] = useState('Role');

  const baseSearchParams: ScoresSearchParams = React.useMemo(() => {
    const searchParams: ScoresSearchParams = {
      search: undefined,
      sortAscending: true,
      page: 1,
      pageSize: 10,
      startDate: undefined,
      endDate: undefined,
      organizationLevel: organizationLevelType,
      companySlug: companyVariables.slug,
      departmentSlugs: undefined,
      teamSlugs: undefined,
      learnerSlug: undefined,
      scoreType: ScoreType.RoleSkill,
      scoreTypeSlugs: undefined,
      scoreComparison: ScoreComparison.RoleExpectedProficiency,
      roleSlug: roleSlug,
    };
    if (!!organizationLevel) {
      if (organizationLevelType === OrganizationLevelType.Department) searchParams.departmentSlugs = organizationLevel.entities.map(entity => entity.slug!);
      if (organizationLevelType === OrganizationLevelType.Team) searchParams.teamSlugs = organizationLevel.entities.map(entity => entity.slug!);
      if (organizationLevelType === OrganizationLevelType.Learner) searchParams.learnerSlug = user?.learnerSlug;
    }
    return searchParams;
  }, [companyVariables.slug, roleSlug, organizationLevelType, organizationLevel]);

  useEffect(() => {
    async function getData() {
      const result = await actions.getCompanyRole({ companySlug: companyVariables.slug!, roleSlug: roleSlug! });
      if (!!result) setRoleName(result?.name ?? 'Role');
    }
    getData();
  }, [companyVariables.slug, roleSlug]);

  return (
    <div className="wrapper stack" style={{ gap: 30 }}>
      <PageHeader
        breadcrumbs={[
          {
            name: 'Analytics',
            url: PagePath.analyticsBase,
          },
          {
            name: 'Roles',
            url: `${PagePath.analyticsBase}${PagePath.analyticsRoles.replace(':companySlug', companyVariables.slug!)}`,
          },
          {
            name: roleName,
            url: window.location.pathname,
          },
        ]}
        title={roleName}
        divider
      />

      <div style={{ width: '100%', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 30 }}>
        <div style={{ flex: '1 1 25%' }}>
          <RoleAverageWidget baseSearchParams={baseSearchParams} />
        </div>

        <div style={{ flex: '2 1 630px' }}>
          <RoleProficiencyRadarWidget baseSearchParams={baseSearchParams} />
        </div>
      </div>

      <SkillScoreExpectedProficiencyWidget baseSearchParams={baseSearchParams} />

      <RoleCompetencyMatrixWidget baseSearchParams={baseSearchParams} />
    </div>
  );
}
